// Daily Exceptions + Miss-punch Corrections inbox for the HR module of
// Khyati Portal / RSR International. The shell renders this component twice with
// different `mode` ('exceptions' | 'corrections').
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import {
  HRC, fmtDate, fmtTime, Card, SectionTitle, SubTabs, Th, Td, Table,
  Pill, Btn, Field, input, Modal, Empty, Loading,
} from './hrkit';

const today = () => new Date().toISOString().slice(0, 10);
const num = (v) => Number(v || 0);

// ───────────────────────────── Exceptions ─────────────────────────────
const EXC_TABS = [
  { key: 'late', label: 'Late Coming', type: 'late' },
  { key: 'miss', label: 'Miss Punch', type: 'miss_punch' },
  { key: 'early', label: 'Outgoing Early', type: 'early' },
  { key: 'absent', label: 'Absentees', type: 'absent' },
];

function ExceptionsView() {
  // Empty until the backend tells us the latest populated day, so the picker
  // opens on a day that actually has data (today is always empty — bridge skips it).
  const [date, setDate] = useState('');
  const [tab, setTab] = useState('late');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ late: 0, miss_punch: 0, early: 0, absent: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const type = EXC_TABS.find((t) => t.key === tab)?.type || 'late';

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setErr('');
    api.get('/hr/attendance/exceptions', { params: { ...(date ? { date } : {}), type } })
      .then((r) => {
        if (!alive) return;
        // First load sends no date; adopt the latest day the backend resolved to.
        if (!date && r.data?.date) setDate(r.data.date);
        setRows(r.data?.data || []);
        const c = r.data?.counts || {};
        setCounts({
          late: num(c.late), miss_punch: num(c.miss_punch),
          early: num(c.early), absent: num(c.absent),
        });
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e.response?.data?.message || 'Could not load exceptions');
        setRows([]);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [date, type]);

  useEffect(load, [load]);

  const badge = { late: counts.late, miss: counts.miss_punch, early: counts.early, absent: counts.absent };
  const tabs = EXC_TABS.map((t) => ({ key: t.key, label: t.label, badge: badge[t.key] }));

  return (
    <Card>
      <SectionTitle right={
        <input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)}
          style={{ ...input, width: 'auto', padding: '7px 10px' }} />
      }>
        Daily Exceptions — {fmtDate(date)}
      </SectionTitle>

      <SubTabs tabs={tabs} active={tab} onChange={setTab} />

      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}

      <div style={{ fontSize: 12, color: HRC.muted, fontWeight: 600, marginBottom: 8 }}>
        {rows.length} employee{rows.length === 1 ? '' : 's'}
      </div>

      {loading ? <Loading /> : rows.length === 0 ? (
        <Empty>
          {tab === 'late' && 'No late arrivals'}
          {tab === 'miss' && 'No miss-punches'}
          {tab === 'early' && 'No early departures'}
          {tab === 'absent' && 'No absentees'}
        </Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Code</Th>
              <Th>Name</Th>
              <Th>Dept</Th>
              {tab === 'late' && (<><Th>Actual In</Th><Th>Late By</Th><Th>Status</Th></>)}
              {tab === 'miss' && (<><Th>In</Th><Th>Out</Th><Th>Note</Th></>)}
              {tab === 'early' && (<><Th>In</Th><Th>Out</Th><Th>Early By</Th></>)}
              {tab === 'absent' && (<Th>Status</Th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <Td style={{ fontWeight: 600 }}>{r.employee_code || '—'}</Td>
                <Td>{r.full_name || '—'}</Td>
                <Td style={{ color: HRC.muted }}>{r.department_name || '—'}</Td>

                {tab === 'late' && (<>
                  <Td>{fmtTime(r.check_in)}</Td>
                  <Td style={{ color: HRC.red, fontWeight: 700 }}>{num(r.late_minutes)} min</Td>
                  <Td><Pill value={r.status} /></Td>
                </>)}

                {tab === 'miss' && (<>
                  <Td>{fmtTime(r.check_in)}</Td>
                  <Td>{fmtTime(r.check_out)}</Td>
                  <Td style={{ color: HRC.amber, fontWeight: 600 }}>Out punch missing</Td>
                </>)}

                {tab === 'early' && (<>
                  <Td>{fmtTime(r.check_in)}</Td>
                  <Td>{fmtTime(r.check_out)}</Td>
                  <Td style={{ color: HRC.amber, fontWeight: 700 }}>{num(r.early_out_minutes)} min</Td>
                </>)}

                {tab === 'absent' && (
                  <Td><Pill value="absent" /></Td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

// ───────────────────────────── Corrections ─────────────────────────────
const COR_TABS = [
  { key: 'pending', label: 'Pending', status: 'pending' },
  { key: 'approved', label: 'Approved', status: 'approved' },
  { key: 'rejected', label: 'Rejected', status: 'rejected' },
  { key: 'all', label: 'All', status: null },
];

const TYPE_LABEL = (t) =>
  String(t || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || '—';

function CorrectionsView({ onChange }) {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);          // id being decided
  const [reject, setReject] = useState(null);      // row pending rejection
  const [note, setNote] = useState('');

  const status = COR_TABS.find((t) => t.key === tab)?.status ?? null;

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setErr('');
    api.get('/hr/corrections', { params: status ? { status } : {} })
      .then((r) => { if (alive) setRows(r.data?.data || r.data || []); })
      .catch((e) => {
        if (!alive) return;
        setErr(e.response?.data?.message || 'Could not load corrections');
        setRows([]);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [status]);

  useEffect(load, [load]);

  const decide = (id, decision, decisionNote) => {
    setBusy(id);
    api.patch(`/hr/corrections/${id}/decision`, { decision, decision_note: decisionNote || '' })
      .then(() => {
        setReject(null);
        setNote('');
        load();
        onChange?.();
      })
      .catch((e) => setErr(e.response?.data?.message || 'Could not save decision'))
      .finally(() => setBusy(null));
  };

  const tabs = COR_TABS.map((t) => ({ key: t.key, label: t.label }));

  return (
    <Card>
      <SectionTitle>Miss-punch Corrections</SectionTitle>
      <SubTabs tabs={tabs} active={tab} onChange={setTab} />

      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}

      <div style={{ fontSize: 12, color: HRC.muted, fontWeight: 600, marginBottom: 8 }}>
        {rows.length} request{rows.length === 1 ? '' : 's'}
      </div>

      {loading ? <Loading /> : rows.length === 0 ? (
        <Empty>No {tab === 'all' ? '' : tab + ' '}correction requests</Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Date</Th>
              <Th>Employee</Th>
              <Th>Dept</Th>
              <Th>Type</Th>
              <Th>Req In</Th>
              <Th>Req Out</Th>
              <Th>Reason</Th>
              <Th>Status</Th>
              <Th style={{ textAlign: 'right' }}>Action</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.work_date)}</Td>
                <Td>
                  <div style={{ fontWeight: 600 }}>{r.full_name || '—'}</div>
                  <div style={{ fontSize: 11, color: HRC.muted }}>{r.employee_code || ''}</div>
                </Td>
                <Td style={{ color: HRC.muted }}>{r.department_name || '—'}</Td>
                <Td>{TYPE_LABEL(r.request_type)}</Td>
                <Td>{fmtTime(r.requested_check_in)}</Td>
                <Td>{fmtTime(r.requested_check_out)}</Td>
                <Td style={{ maxWidth: 220, whiteSpace: 'normal', color: HRC.muted }}>{r.reason || '—'}</Td>
                <Td><Pill value={r.status} /></Td>
                <Td style={{ textAlign: 'right' }}>
                  {r.status === 'pending' ? (
                    <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                      <Btn size="sm" variant="primary" disabled={busy === r.id}
                        style={{ background: HRC.green }}
                        onClick={() => decide(r.id, 'approved')}>Approve</Btn>
                      <Btn size="sm" variant="danger" disabled={busy === r.id}
                        onClick={() => { setReject(r); setNote(''); }}>Reject</Btn>
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: HRC.muted, textAlign: 'right' }}>
                      {r.reviewer_name ? <div>by {r.reviewer_name}</div> : null}
                      {r.reviewed_at ? <div>{fmtDate(r.reviewed_at)}</div> : null}
                      {r.decision_note ? <div style={{ fontStyle: 'italic' }}>“{r.decision_note}”</div> : null}
                    </div>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {reject && (
        <Modal title="Reject correction" width={420} onClose={() => { setReject(null); setNote(''); }}>
          <div style={{ fontSize: 13, color: HRC.text, marginBottom: 4 }}>
            {reject.full_name} · {fmtDate(reject.work_date)}
          </div>
          <div style={{ fontSize: 12, color: HRC.muted, marginBottom: 14 }}>
            {TYPE_LABEL(reject.request_type)} — {reject.reason || 'no reason given'}
          </div>
          <Field label="Decision note (optional)">
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              placeholder="Why is this being rejected?"
              style={{ ...input, resize: 'vertical' }} />
          </Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <Btn variant="ghost" onClick={() => { setReject(null); setNote(''); }}>Cancel</Btn>
            <Btn variant="danger" disabled={busy === reject.id}
              onClick={() => decide(reject.id, 'rejected', note)}>Reject request</Btn>
          </div>
        </Modal>
      )}
    </Card>
  );
}

// ───────────────────────────── Entry ─────────────────────────────
export default function Exceptions(props) {
  const { mode, onChange } = props || {};
  if (mode === 'corrections') return <CorrectionsView onChange={onChange} />;
  return <ExceptionsView />;
}
